import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  Backdrop,
  CircularProgress,
  styled,
} from "@mui/material";
import axios from "axios";
import toast from 'react-hot-toast';

const CardTopUpDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [coin, setCoin] = useState("");
  const [network, setNetwork] = useState("");
  const [balance, setBalance] = useState(null);
  const [baseFee, setBaseFee] = useState(null);
  const [minFee, setMinFee] = useState(null);
  const [maxFee, setMaxFee] = useState(null);
  const [percentageFee, setPercentageFee] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [remainingAmount, setRemainingAmount] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [sourceAddress, setSourceAddress] = useState(null);
  const [loading, setLoading] = useState(false); // State for loading screen
  const [fee, setFee] = useState(0); // State for fee
  const [exchangeRate, setExchangeRate] = useState(null); // State for exchange rate
  const [defaultExchangeRate, setDefaultExchangeRate] = useState(0); // State for default exchange rate
  const [balanceinUSD, setBalanceinUSD] = useState(0); // State for balance in USD
  const [convertedAmount, setConvertedAmount] = useState(0); // State for converted amount

  // Reset all states when the dialog is opened
  useEffect(() => {
    if (open) {
      setStep(1);
      setCoin("");
      setNetwork("");
      setBalance(null);
      setBaseFee(null);
      setMinFee(null);
      setMaxFee(null);
      setPercentageFee(null);
      setAmount("");
      setDescription("");
      setRemainingAmount(null);
      setAssetId(null);
      setSourceAddress(null);
      setFee(0);
      setExchangeRate(null);
      setDefaultExchangeRate(0);
      setBalanceinUSD(0);
      setConvertedAmount(0);
      setLoading(false); // Reset loading state
    }
  }, [open]);

  const handleNext = async () => {
    if (step === 1) {
      try {
        setLoading(true); // Show loading screen
  
        // Reset fee and balance
        setFee(0);
        setBalance(0);
        setBalanceinUSD(0);
        setBaseFee(0);
        setMinFee(0);
        setMaxFee(0);
        setPercentageFee(0);
        setAssetId(null);
        setSourceAddress(null);
        setExchangeRate(null);
        setDefaultExchangeRate(0);
  
        const email = Cookies.get("email");
  
        // Fetch balance and fee from API
        const response = await axios.get(`/api/api/getBalance/${email}/${coin}/${network}`);

        if (response.data.status === "success") {
          const fetchedBalance = response.data.balance; // Use the fetched balance directly
          const getExchangeRate = await axios.get(`/api/api/card/exchangeRate/${coin}`);
          if (getExchangeRate.data.status === "success") {
            const fetchedExchangeRate = getExchangeRate.data.rate;
            const fetchedDefaultExchangeRate = getExchangeRate.data.defaultRate;
  
            // Update state with fetched values
            setBalance(fetchedBalance);
            setExchangeRate(fetchedExchangeRate);
            setDefaultExchangeRate(fetchedDefaultExchangeRate);
  
            // Perform calculations using fetched values directly
            const calculatedBalanceInUSD = fetchedBalance * fetchedDefaultExchangeRate;
            setBalanceinUSD(calculatedBalanceInUSD);

          } else {
            console.error("Error fetching exchange rate:", getExchangeRate.data);
          }
  
          // Update fee-related states
          setBaseFee(response.data.baseFee);
          setMinFee(response.data.minFee);
          setMaxFee(response.data.maxFee);
          setPercentageFee(response.data.percentage);
          setAssetId(response.data.assetId);
          setSourceAddress(response.data.assetAddress);
  
          setStep(2); // Move to the next step
        } else {
          console.error("Error fetching balance and fee:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching balance and fee:", error);
      } finally {
        setLoading(false); // Hide loading screen
      }
    } else if (step === 2) {
      // Calculate remaining amount
      const remaining = balance - amount - fee;
      setRemainingAmount(remaining);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true); // Show loading screen
      // Submit top-up request
      const response = await axios.post(`/api/api/card/topup`, {
        coin,
        network,
        amount,
        description,
        assetId,
        sourceAddress,
        email: Cookies.get("email"),
        cardId: Cookies.get("cardTokenId") || null,
      });

      onClose(); // Close the dialog after submission
      toast.success("Top-up request submitted successfully!");
    } catch (error) {
      console.error("Error submitting top-up:", error);
      toast.error("Error submitting top-up request. Please try again.");
    } finally {
      setLoading(false); // Hide loading screen
    }
  };

  // function for calculate fee against amount
  const calculateFee = (amount) => {
    let fee = 0;
  
    // Add base fee
    if (baseFee) fee += parseFloat(baseFee);
  
    // Add percentage fee if it's greater than 0
    if (percentageFee && parseFloat(percentageFee) > 0) {
      fee += (parseFloat(percentageFee) / 100) * parseFloat(amount);
    }
  
    // Check against min fee if it's greater than 0
    if (minFee && parseFloat(minFee) > 0 && fee < parseFloat(minFee)) {
      return parseFloat(minFee);
    }
  
    // Check against max fee if it's greater than 0
    if (maxFee && parseFloat(maxFee) > 0 && fee > parseFloat(maxFee)) {
      return parseFloat(maxFee);
    }
  
    // Return calculated fee
    return fee;
  };

  const handleAmountChange = (e) => {
    const enteredValue = e.target.value;

    // If the input is empty, set the amount to an empty string
    if (enteredValue === "") {
        setAmount("");
        setConvertedAmount(0); // Reset converted amount
        setFee(0); // Reset fee
        return;
    }

    // Parse the entered value as a float
    const enteredAmount = parseFloat(enteredValue) || 0;
    setAmount(enteredAmount);

    // Convert amount to coin value
    setConvertedAmount(amountConvert(enteredAmount));

    // Calculate the fee using the calculateFee function
    const calculatedFee = calculateFee(enteredAmount);
    setFee(calculatedFee);
};

  // convert amount to USD
  const amountConvert = (amount) => {
    if (exchangeRate) {
      return (amount * exchangeRate).toFixed(10);
    }
    return 0;
  };

  const StyledFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#542374',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#542374',
      },
    },
    '& .MuiInputLabel-root': {
      backgroundColor: '#ffffff', // Set background color to white
      padding: '0 4px', // Add padding to prevent overlap with the border
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#542374',
    },
  }));

  const StyledMenuItem = styled(MenuItem)({
    padding: '10px 16px',
    '&.Mui-selected': {
      backgroundColor: 'rgba(84, 35, 116, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(84, 35, 116, 0.12)'
      }
    }
  });

  return (
    <>
      {/* Loading Screen */}
      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Top Up Card</DialogTitle>
        <DialogContent>
          {step === 1 && (
            <Box>
              <StyledFormControl fullWidth margin="normal">
                <InputLabel>Coin</InputLabel>
                <Select value={coin} onChange={(e) => setCoin(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        mt: 1,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      },
                    },
                  }}
                  >
                  <StyledMenuItem value="usdt">USDT</StyledMenuItem>
                  <StyledMenuItem value="usdc">USDC</StyledMenuItem>
                </Select>
              </StyledFormControl>
              <StyledFormControl fullWidth margin="normal">
                <InputLabel>Network</InputLabel>
                <Select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        mt: 1,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      },
                    },
                  }}
                >
                  <StyledMenuItem value="erc20">ERC20</StyledMenuItem>
                  <StyledMenuItem value="trc20">TRC20</StyledMenuItem>
                </Select>
              </StyledFormControl>
            </Box>
          )}
          {step === 2 && (
            <Box>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "12px", mt: 2 }}
              >
                Available Balance in {coin.toUpperCase()}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {balance} {coin.toUpperCase()}
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "12px", mt: 2 }}
              >
                Available Balance in USD
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {balanceinUSD > 0
                  ? `${balanceinUSD} USD`
                  : "0.00 USD"}
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "12px", mt: 2 }}
              >
                Exchange Rate
              </Typography>
              <Typography variant="body1">
                1 USD = {exchangeRate} {coin.toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 4 }}>
                ~ {defaultExchangeRate} USD
              </Typography>
              <TextField
                fullWidth
                label="Topup Amount in USD"
                type="number" // Use text to allow validation
                value={amount}
                onChange={handleAmountChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "12px", mt: 2 }}
              >
                Network Fee
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {fee} USD
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "12px", mt: 2 }}
              >
                You will be charged
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {convertedAmount} {coin.toUpperCase()}
              </Typography>
              {amount && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ fontSize: "12px" }}
                  >
                    Topup Amount you will be get
                  </Typography>
                  <Typography variant="body1">
                    {balanceinUSD - amount > 0
                      ? `${
                          amount - fee > 0 ? (amount - fee).toFixed(2) : "0.00"
                        } USD`
                      : "Insufficient Balance"}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {step === 2 && (
            <Button onClick={() => setStep(1)} variant="outlined">
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!coin || !network}
              loading={loading}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={
                !amount ||
                balanceinUSD - amount < 0 ||
                amount - fee < 0 ||
                !description
              }
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CardTopUpDialog;